import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import { useChangeCampaignStateMutation } from 'modules/campaigns/lib';
import { getPreviewHTMLTemplateUrl } from 'modules/campaigns/lib/utils/template';
import { CampaignState } from 'modules/domains/campaign/types';
import { MessageMedium } from 'modules/graphql/types';
import { useDisplayPriorityContext } from 'modules/placements/context/DisplayPriorityContext';
import { PREVIEW_IN_DEVICE_URL } from 'modules/templateEditor/common/constants';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { CampaignSendTestModalAndTrigger } from 'modules/templateEditor/integrations/CampaignSendTestModalAndTrigger';
import { useTemplateTakeover } from 'modules/templateEditor/integrations/InStudioTemplateEditing/context';
import { ActionMenu } from 'ui/actionMenu';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_SIZES, BUTTON_VARIANTS, PrimaryButton, SecondaryButton } from 'ui/button';
import { ChevronDown24, LongArrowRight, OpenNewWindow16, View24 } from 'ui/icons';
import { colors, spacing } from 'ui/theme';

import {
  ModalType,
  useCampaignContext,
  useEditorContext,
  useModalContext,
  useTemplateContext,
  useUnsavedPromptContext,
} from '../../../context';
import { StyledButtons } from '../../../styles';
import { CampaignEditorView } from '../../../types';
import { StyledOverflowButton } from '../../CampaignCard/CampaignCard.styled';
import { CampaignPopover } from '../../CampaignPopover';
import {
  getHasDisplayPriority,
  getIsDefaultWhatsAppTemplate,
  getIsEligibilityCampaign,
} from '../../utils';

const ComboButton = styled.div<{ hasDesignError?: boolean }>`
  display: flex;
  > button:first-child {
    border-right: none;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
    ${({ hasDesignError }) => hasDesignError && `border: 2px solid ${colors.red100}`}
  }
  > *:last-child > button {
    border-left: none;
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }
`;

const StyledActionMenu = styled(ActionMenu)`
  a {
    display: inline-flex;
    gap: ${spacing.half};
  }
`;

export const EditViewActions = ({ hasReviewButton = true }) => {
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const takeover = useTemplateTakeover();
  const { setCampaignEditorView, setIsDisplayPriorityTakeoverOpen } = useEditorContext();
  const { templateAndContent } = useTemplateContext();
  const { templateType } = templateAndContent;
  const { campaign, refetch: campaignRefetch } = useCampaignContext();
  const { messageMedium } = campaign;
  const { transitionTo } = useUnsavedPromptContext();
  const { closeModal, openModal } = useModalContext();
  const {
    reset: setInitialDisplayPriority,
    saveDisplayPriority,
    campaignDisplayPriorityOrder,
  } = useDisplayPriorityContext();

  const { mutate } = useChangeCampaignStateMutation({
    onSuccess: () => {
      campaignRefetch();
    },
  });

  const { locale } = useQueryParamContext();

  const { templateId } = campaign;

  const previewOnDeviceUrl = `${PREVIEW_IN_DEVICE_URL}?templateId=${templateId}`;
  const previewHTMLUrl = getPreviewHTMLTemplateUrl(templateId, locale); // "Preview in new tab" url
  const isEmailCampaign = campaign?.messageMedium === MessageMedium.Email;
  const hasValidContent = templateAndContent?.validationFailures?.errors?.length === 0;

  const isMissingDisplayPriority =
    isEmbeddedMessagingEnabled &&
    getIsEligibilityCampaign(campaign?.campaignTypeDefinition) &&
    !getHasDisplayPriority(campaign?.embeddedPriorityOrder);

  const isDefaultWhatsAppTemplate = getIsDefaultWhatsAppTemplate(messageMedium, templateType);

  const handleTransitionToSummary = () => {
    campaignRefetch();
    if (campaign.campaignState === CampaignState.Draft) {
      mutate({ campaignId: campaign.id, campaignState: CampaignState.Ready });
    }
    transitionTo(() => setCampaignEditorView(CampaignEditorView.Summary));
  };

  const handleConfirmDefaultDisplayPriority = () => {
    setInitialDisplayPriority();
    saveDisplayPriority();
    closeModal(ModalType.ConfirmDefaultDisplayPriorityModal);

    handleTransitionToSummary();
  };

  const handleSelectDisplayPriority = () => {
    closeModal(ModalType.ConfirmDefaultDisplayPriorityModal);
    transitionTo(() => setIsDisplayPriorityTakeoverOpen(true));
  };

  const handleReviewCampaignClick = () => {
    if (isMissingDisplayPriority)
      openModal(ModalType.ConfirmDefaultDisplayPriorityModal, {
        campaignDisplayPriorityOrder,
        onClose: () => closeModal(ModalType.ConfirmDefaultDisplayPriorityModal),
        onConfirm: handleConfirmDefaultDisplayPriority,
        onSelectDisplayPriority: handleSelectDisplayPriority,
      });
    else handleTransitionToSummary();
  };

  const previewOptions: ActionMenuOption[] = [
    {
      as: AllowedActionMenuElements.Link,
      content: (
        <>
          Preview in new tab
          <OpenNewWindow16 />
        </>
      ),
      id: 'btn-edit-design-preview-in-new-tab',
      href: previewHTMLUrl,
      target: '_blank',
      isExternalLink: true,
    },
    {
      as: AllowedActionMenuElements.Link,
      content: (
        <>
          Preview on devices
          <OpenNewWindow16 />
        </>
      ),
      id: 'btn-edit-design-preview-on-devices',
      href: previewOnDeviceUrl,
      target: '_blank',
      isExternalLink: true,
    },
  ];

  const MainPreviewButton = ({ isDisabled = false }) => (
    <SecondaryButton
      data-test="preview-button"
      isDisabled={isDisabled}
      size={BUTTON_SIZES.LARGE}
      onClick={() => takeover.open(takeover.viewOptions.preview)}
    >
      <View24 /> Preview
    </SecondaryButton>
  );

  return (
    <StyledButtons data-test="edit-view-actions">
      <CampaignSendTestModalAndTrigger campaign={campaign} />

      {/* Combo button or a single preview button */}
      {isEmailCampaign ? (
        <ComboButton>
          <MainPreviewButton />
          <StyledActionMenu align={MENU_ALIGNMENTS.RIGHT} options={previewOptions}>
            <StyledOverflowButton
              data-test="email-preview-combo-button"
              isDisabled={!hasValidContent}
              size={BUTTON_SIZES.LARGE}
              variant={BUTTON_VARIANTS.SECONDARY}
            >
              <ChevronDown24 />
            </StyledOverflowButton>
          </StyledActionMenu>
        </ComboButton>
      ) : (
        <MainPreviewButton isDisabled={isDefaultWhatsAppTemplate} />
      )}

      {!!hasReviewButton && (
        <CampaignPopover>
          <PrimaryButton
            data-test="review-button"
            size={BUTTON_SIZES.LARGE}
            onClick={handleReviewCampaignClick}
          >
            Review <LongArrowRight />
          </PrimaryButton>
        </CampaignPopover>
      )}
    </StyledButtons>
  );
};

export const EditViewActionsOnReviewPage = () => <EditViewActions hasReviewButton={false} />;
